import { WinDataType } from '@/utils/types/window';
import dynamic from 'next/dynamic';

// Define your component props type
export type WindowComponentProps = {
  windowTitle: string;
  windowId: number;
  winData?: WinDataType;
  selectedAssetId: string | null;
  onAssetSelect: (assetId: string) => void;
  isComponentLoading: boolean;
  setIsComponentLoading: (loading: boolean) => void;
  handlePrintWindow: () => void;
  beforeCloseRef?: React.MutableRefObject<(() => Promise<boolean | void>) | null>;
  disableClose?: boolean;
  disableMessage?: string[];
  isFocused?: boolean;
};

const createDynamicComponent = (
  importPath: () => Promise<{ default: React.ComponentType<WindowComponentProps> }>,
): React.ComponentType<WindowComponentProps> => dynamic(importPath, { ssr: false });

const componentRegistry: Record<string, React.ComponentType<WindowComponentProps>> = {
  AccessoriesPhotoDetail: createDynamicComponent(
    () => import('@/components/module/accessoriesPhotoDetail/AccessoriesPhotoDetail'),
  ),
  accysPhoto: createDynamicComponent(() => import('@/components/module/accessory_photo/AccessoryPhoto')),
  accessoryPackage: createDynamicComponent(
    () => import('@/components/module/manuf_model_accessory_package/AccessoryPackage'),
  ),
  accessoryPhotoList: createDynamicComponent(
    () => import('@/components/module/accessory_photo_list/AccessoryPhotoList'),
  ),
  accessoryStatus: createDynamicComponent(() => import('@/components/module/accessory_status/AccessoryStatus')),
  accessorizing: createDynamicComponent(() => import('@/components/module/accessorizing/Accessorizing')),
  addAccessories: createDynamicComponent(() => import('@/components/module/add_accessories/AddAccessories')),
  addANewRepairOrder: createDynamicComponent(
    () => import('@/components/module/add_new_repair_order/AddANewRepairOrder'),
  ),
  addCalCard: createDynamicComponent(() => import('@/components/module/view_cal_card/ViewCalCard')),
  affectedItems: createDynamicComponent(() => import('@/components/module/affected_items/AffectedItems')),
  asset: createDynamicComponent(() => import('@/components/module/asset/Asset/Asset')),
  assetAccys: createDynamicComponent(() => import('@/components/module/accessories/Accessories')),
  assetComment: createDynamicComponent(() => import('@/components/module/asset/AssetComment')),
  assetCommentView: createDynamicComponent(() => import('@/components/module/asset/AssetCommentView')),
  assetFinancial: createDynamicComponent(() => import('@/components/module/asset/AssetFinancial')),
  assetGeneral: createDynamicComponent(() => import('@/components/module/asset/AssetGeneral')),
  assetMargin: createDynamicComponent(() => import('@/components/module/asset_margin/AssetMargin')),
  assetMergeAndLink: createDynamicComponent(() => import('@/components/module/asset_merge_and_link/AssetMergeAndLink')),
  assetOwnership: createDynamicComponent(() => import('@/components/module/asset_ownership/AssetOwnership')),
  assetOwnershipTransfer: createDynamicComponent(() => import('@/components/module/asset/AssetOwnerShipHistory')),
  assetSearch: createDynamicComponent(() => import('@/components/module/asset_search/AssetSearch')),
  assignInvoiceEmailLevels: createDynamicComponent(
    () => import('@/components/module/AssignInvoiceEmailLevels/AssignInvoiceEmailLevels'),
  ),
  auditTrail: createDynamicComponent(() => import('@/components/module/auditTrail/auditTrail')),
  avlInspection: createDynamicComponent(() => import('@/components/module/avl_inspection/AVLInspection')),
  badDebtWriteOff: createDynamicComponent(() => import('@/components/module/badDebtWriteOff/BadDebtWriteOff')),
  badDebtWriteOffApproval: createDynamicComponent(
    () => import('@/components/module/bad_debt_write_off_approval/BadDebtWriteOffApproval'),
  ),
  bdwoSignoff: createDynamicComponent(() => import('@/components/module/BDWOSignoff/BDWOSignoff')),
  browseImage: createDynamicComponent(() => import('@/components/module/browse_image/BrowseImage')),
  buyoutQuoteApproval: createDynamicComponent(
    () => import('@/components/module/buyout_quote_approval/BuyoutQuoteApproval'),
  ),
  buyoutQuoteSignoff: createDynamicComponent(
    () => import('@/components/module/buyout_quote_signoff/BuyoutQuoteSignoff'),
  ),
  cblob: createDynamicComponent(() => import('@/components/module/CBlob')),
  cblobContent: createDynamicComponent(() => import('@/components/module/CBlobContent')),
  changeMfgModel: createDynamicComponent(() => import('@/components/module/asset/ChangeMfgAsset')),
  changePoLineData: createDynamicComponent(() => import('@/components/module/change_po_line_data/ChangePOLine')),
  changePurchaseRequestData: createDynamicComponent(
    () => import('@/components/module/change_purchase_request_data/ChangePurchaseRequestLineData'),
  ),
  changeRaTransferDetailData: createDynamicComponent(
    () => import('@/components/module/change_ra_transfer_detail_data/ChangeRaTransferDetailData'),
  ),
  changeWaybillNumber: createDynamicComponent(() => import('@/components/module/change_waybill_number/ChangeWayBill')),
  compareTaxStat: createDynamicComponent(() => import('@/components/module/compare_tax_stat/CompareTaxStat')),
  creditApproval: createDynamicComponent(() => import('@/components/module/credit_approval/CreditApproval')),
  creditCardAuthorization: createDynamicComponent(
    () => import('@/components/module/creditCardAuthorization/CreditCardAuthorization'),
  ),
  currencyCalculator: createDynamicComponent(() => import('@/components/module/currency_cal/CurrencyCal')),
  customer: createDynamicComponent(() => import('@/components/module/customer/Customer')),
  customerAddress: createDynamicComponent(() => import('@/components/module/customer_address/CustomerAddress')),
  customerComment: createDynamicComponent(() => import('@/components/module/customer_comment/CustomerComment')),
  customerInformation: createDynamicComponent(
    () => import('@/components/module/customer_information/CustomerInformation'),
  ),
  customerPurchaseOrder: createDynamicComponent(
    () => import('@/components/module/customer_purchase_order/CustomerPurchaseOrder'),
  ),
  dairy: createDynamicComponent(() => import('@/components/module/dairy/Dairy')),
  duplicateAddress: createDynamicComponent(() => import('@/components/module/duplicateAddress/DuplicateAddress')),
  editCalCard: createDynamicComponent(() => import('@/components/module/view_cal_card/ViewCalCard')),
  enterWaybill: createDynamicComponent(() => import('@/components/module/enterWaybill/enterWaybill')),
  eSignPOView: createDynamicComponent(
    () => import('@/components/module/purchase_order/purchase_order_view/components/PO_ESign'),
  ),
  exposure: createDynamicComponent(() => import('@/components/module/Exposure/Exposure')),
  fillRequests: createDynamicComponent(() => import('@/components/module/fill_requests/FillRequests')),
  freight: createDynamicComponent(() => import('@/components/module/freight/FreightForm')),
  frightInvoice: createDynamicComponent(() => import('@/components/module/fright_invoice/FrightInvoice')),
  frmRtaNew: createDynamicComponent(() => import('@/components/module/frmRtaNew/frmRtaNew')),
  geoCodeAddressOptions: createDynamicComponent(
    () => import('@/components/module/geo_code_address_options/GeoCodeAddressOptions'),
  ),
  groupConfig: createDynamicComponent(() => import('@/components/module/group_config/GroupConfig')),
  invoiceAndDeliveryLevels: createDynamicComponent(
    () => import('@/components/module/invoice_and_delivery_levels/InvoiceAndDeliveryLevels'),
  ),
  invoiceScan: createDynamicComponent(() => import('@/components/module/customer_invoice_scan/CustomerInvoiceScan')),
  labels: createDynamicComponent(() => import('@/components/module/barcodes/Barcodes')),
  labFunction: createDynamicComponent(() => import('@/components/module/lab_function/LabFunction')),
  manufacturer: createDynamicComponent(() => import('@/components/module/manufacturer/Manufacturer')),
  modelInformation: createDynamicComponent(
    () => import('@/components/module/newOrUnavailableModelInformation/NewOrUnavailableModelInformation'),
  ),
  modelReservation: createDynamicComponent(() => import('@/components/module/ModelReservation/ModelReservation')),
  movementControl: createDynamicComponent(() => import('@/components/module/movement_control/MovementControl')),
  mpsAdvanceUpdate: createDynamicComponent(() => import('@/components/module/MpsAdvanceUpdate/MpsAdvanceUpdate')),
  mrhOverride: createDynamicComponent(() => import('@/components/module/mrh_override/MrhOverride')),
  mrRequestorSignIn: createDynamicComponent(() => import('@/components/module/MRRequestorSignIn/MRRequestorSignIn')),
  packing: createDynamicComponent(() => import('@/components/module/packing/Packing')),
  partsAccys: createDynamicComponent(() => import('@/components/module/parts_accys/PartsAccysForm')),
  partsAccessoriesVendorDetails: createDynamicComponent(
    () => import('@/components/module/parts_accessories_vendor_details/PartsAccessoriesVendorDetails'),
  ),
  partsOrAccessoriesReceiving: createDynamicComponent(
    () => import('@/components/module/partsor_acessories_receiving/PartsOrAccessoriesReceiving'),
  ),
  pendingBacklog: createDynamicComponent(() => import('@/components/module/pending_backlog/PendingBacklog')),
  pendingManfModel: createDynamicComponent(() => import('@/components/module/pending_manf_model/PendingManfModel')),
  pmQuoteReview: createDynamicComponent(() => import('@/components/module/pm_quote_review/PmQuoteReview')),
  exampleDualDropdown: createDynamicComponent(() => import('@/components/module/ExampleDualDropdown')),
  exampleInfiniteScrollTable: createDynamicComponent(() => import('@/components/module/ExampleInfiniteScrollTable')),
  oscar: createDynamicComponent(() => import('@/components/module/oscar/Oscar')),
  poCreate: createDynamicComponent(
    () => import('@/components/module/purchase_order/purchase_order_create/PurchaseOrderCreate'),
  ),
  poDetails: createDynamicComponent(() => import('@/components/module/po_details/PoDetails')),
  poPrintConfirmation: createDynamicComponent(
    () => import('@/components/module/po_print_confirmation/PoPrintConfirmation'),
  ),
  poView: createDynamicComponent(
    () => import('@/components/module/purchase_order/purchase_order_view/PurchaseOrderView'),
  ),
  purchaseRequest: createDynamicComponent(() => import('@/components/module/purchase_request/PurchaseRequest')),
  raAssetAccessories: createDynamicComponent(
    () => import('@/components/module/ra_asset_accessories/RaAssetAccessories'),
  ),
  raAssetExchange: createDynamicComponent(() => import('@/components/module/ra_asset_exchange/RaAssetExchange')),
  raCompTransfer: createDynamicComponent(() => import('@/components/module/ra_comp_transfer/RaCompTransfer')),
  raDtlChangeRADetailData: createDynamicComponent(
    () => import('@/components/module/ra_dtl_change_radetail_data/RaDtlChangeRADetailData'),
  ),
  receiving: createDynamicComponent(
    () => import('@/components/module/asset_accessories_receiving/AssetAccessoriesReceiving'),
  ),
  receivingFreight: createDynamicComponent(() => import('@/components/module/receiving_freight/ReceivingFreight')),
  receiveAndTerminateScreen: createDynamicComponent(
    () => import('@/components/module/ReceiveAndTerminateScreen/ReceiveAndTerminateScreen'),
  ),
  rentalAgreement: createDynamicComponent(() => import('@/components/module/rental_agreement/RentalAgreement')),
  reserveAssets: createDynamicComponent(() => import('@/components/module/reserve_assets/ReserveAssets')),
  reserveByModel: createDynamicComponent(() => import('@/components/module/reserve_by_model/ReserverModel')),
  reserveUnreserveAsset: createDynamicComponent(
    () => import('@/components/module/reserve_unreserve_asset/ReserveUnreserveAsset'),
  ),
  rfpAssetList: createDynamicComponent(() => import('@/components/module/rfp_asset_list/RfpAssetList')),
  riskCode: createDynamicComponent(() => import('@/components/module/risk_code/RiskCode')),
  rta: createDynamicComponent(() => import('@/components/module/rta/RTA/Rta')),
  rtaFreight: createDynamicComponent(() => import('@/components/module/rta/RtaFreightInfo')),
  scanCustomer: createDynamicComponent(() => import('@/components/module/scanCustomer/ScanCustomer')),
  scanDairyByRa: createDynamicComponent(() => import('@/components/module/dairy/ScanDairyByRA')),
  scanInAssets: createDynamicComponent(() => import('@/components/module/scan_in_assets/ScanAssets')),
  selectPartsAccessoriesModal: createDynamicComponent(
    () => import('@/components/module/add_new_repair_order/components/SelectPartsAccessoriesModal'),
  ),
  selectProcedure: createDynamicComponent(() => import('@/components/module/SelectProcedure/SelectProcedure')),

  hotlineSignOff: createDynamicComponent(() => import('@/components/module/hotline_sign_off/HotlineSignOff')),
  specialComments: createDynamicComponent(() => import('@/components/module/special_comments/SpecialComments')),
  specialInstructions: createDynamicComponent(
    () => import('@/components/module/special_instructions/SpecialInstructions'),
  ),
  taxRates: createDynamicComponent(() => import('@/components/module/tax_rate/TaxRate')),
  invoiceEmailMaintenance: createDynamicComponent(
    () => import('@/components/module/invoiceEmailMaintenance/InvoiceEmailMaintenance'),
  ),
  technicalLibraryBrowseFacility: createDynamicComponent(
    () => import('@/components/module/technical _library_browse_facility/TechnicalLibraryBrowseFacility'),
  ),
  unSellRequest: createDynamicComponent(() => import('@/components/module/UnSellRequest/UnSellRequest')),
  updateManufacturer: createDynamicComponent(() => import('@/components/module/manufacturer/ManufacturerUpdate')),
  updateSoftwareFirmware: createDynamicComponent(
    () => import('@/components/module/update_software_firmware/UpdateSoftwareFirmware'),
  ),
  userRoleSetup: createDynamicComponent(() => import('@/components/module/user_role_setup/User')),
  vendor: createDynamicComponent(() => import('@/components/module/vendor/Vendor')),
  vendorPurchaseOrder: createDynamicComponent(
    () => import('@/components/module/vendorPurchaseOrder/vendorPurchaseOrder'),
  ),
  verifyModelDataForQuote: createDynamicComponent(
    () => import('@/components/module/verify_model_data_for_quote/VerifyModelDataForQuote'),
  ),
  VerifyPartRequest: createDynamicComponent(() => import('@/components/module/VerifyPartRequest/verifyPartRequest')),
  verifyPurchaseRequest: createDynamicComponent(
    () => import('@/components/module/verifyPurchaseRequest/VerifyPurchaseRequest'),
  ),
  viewCalCard: createDynamicComponent(() => import('@/components/module/view_cal_card/ViewCalCard')),
  viewNotification: createDynamicComponent(() => import('@/components/common/ViewNotification')),
  raPullAsset: createDynamicComponent(() => import('@/components/module/ra_pull_asset/raPullAsset')),
  raPullAssetWareHouse: createDynamicComponent(() => import('@/components/module/ra_pull_asset/raPullAsset')),
  raLstLostComponent: createDynamicComponent(
    () => import('@/components/module/ra_lst_lost_component/RaLstLostComponent'),
  ),
  contact: createDynamicComponent(() => import('@/components/module/contact/Contact')),
  creditSignOff: createDynamicComponent(() => import('@/components/module/credit_sign_off/CreditSignOff')),
  rolePermission: createDynamicComponent(() => import('@/components/module/role_permission/RolePermission')),
  roles: createDynamicComponent(() => import('@/components/module/roles/Roles')),
  credit: createDynamicComponent(() => import('@/components/module/credit/Credit')),
  raCreditCard: createDynamicComponent(() => import('@/components/module/ra_credit_card/RaCreditCard')),
  billAdjustInvoices: createDynamicComponent(() => import('@/components/module/billAdjustInvoices/BillAdjustInvoices')),
  startPage: createDynamicComponent(() => import('@/components/module/start_page/StartPage')),
  changeShipToComponent: createDynamicComponent(
    () => import('@/components/module/change_ship_to_component/ChangeShipToComponent'),
  ),
  displayLostModels: createDynamicComponent(() => import('@/components/module/display_lost_models/DisplayLostModels')),
  historyByModel: createDynamicComponent(() => import('@/components/module/history_by_model/HistoryByModel')),
  commitAsset: createDynamicComponent(() => import('@/components/module/commitAsset/CommitAsset')),
  moveTo: createDynamicComponent(() => import('@/components/module/move_to/MoveTo')),
  raList: createDynamicComponent(() => import('@/components/module/ra_list/raList')),
  frmFastTrack: createDynamicComponent(() => import('@/components/module/frm_fast_track/frmFastTrack')),
  webOrdersAndBadRFQ: createDynamicComponent(
    () => import('@/components/module/web_orders_and_web_rfq/WebOrdersAndWebRFQ'),
  ),
  modelReservationQuery: createDynamicComponent(
    () => import('@/components/module/ModelReservationQuery/ModelReservationQuery'),
  ),
  priceApproval: createDynamicComponent(() => import('@/components/module/priceApproval/PriceApproval')),
  queryTAF: createDynamicComponent(() => import('@/components/module/rental_agreement/QueryTAF')),
  priceChangeRequest: createDynamicComponent(() => import('@/components/module/priceChangeRequest/PriceChangeRequest')),
  match: createDynamicComponent(() => import('@/components/module/match/Match')),
  priceApprovalDetails: createDynamicComponent(
    () => import('@/components/module/price_approval_details/PriceApprovalDetails'),
  ),
  priceApprovalSignOff: createDynamicComponent(
    () => import('@/components/module/price_approval_sign_off/PriceApprovalSignOff'),
  ),
  sortFields: createDynamicComponent(() => import('@/components/module/sort_fields/SortFields')),
  billingAdjustment: createDynamicComponent(() => import('@/components/module/billing_adjustment/BillingAdjustment')),
  matchData: createDynamicComponent(() => import('@/components/module/match_data/MatchData')),
  technicalLibraryMaintenance: createDynamicComponent(
    () => import('@/components/admin_forms/technical_library_maintenance/TechnicalLibraryMaintenance'),
  ),
  mrUser: createDynamicComponent(() => import('@/components/admin_forms/mr_user/MrUser')),
  taxCertificatesInformation: createDynamicComponent(
    () => import('@/components/admin_forms/tax_certificates_information/TaxCertificatesInformation'),
  ),
  locationCodes: createDynamicComponent(() => import('@/components/admin_forms/location_codes/LocationCodes')),
  temperatureAndHumidity: createDynamicComponent(
    () => import('@/components/admin_forms/temperature_and_humidity/TemperatureAndHumidity'),
  ),
  timeWorkedMaintenance: createDynamicComponent(
    () => import('@/components/admin_forms/time_worked_maintenance/TimeWorkedMaintenance'),
  ),
  manufacturerModelInformation: createDynamicComponent(
    () => import('@/components/admin_forms/manufacturer_model_information/ManufacturerModelInformation'),
  ),
  selectCompany: createDynamicComponent(() => import('@/components/admin_forms/select_company/selectCompany')),
  nistLabStandards: createDynamicComponent(
    () => import('@/components/admin_forms/nist_lab_standards/NistLabStandards'),
  ),
  about: createDynamicComponent(() => import('@/components/module/about/About')),
  version: createDynamicComponent(() => import('@/components/admin_forms/version/Version')),
  hotline: createDynamicComponent(() => import('@/components/module/hotline/Hotline')),
  home: createDynamicComponent(() => import('@/components/module/home/Home')),
  territory: createDynamicComponent(() => import('@/components/admin_forms/territory/Territory')),
  achProcess: createDynamicComponent(() => import('@/components/admin_forms/ach_process/AchProcess')),
  ccProcess: createDynamicComponent(() => import('@/components/admin_forms/cc_process/CcProcess')),
  orderApprovalLimits: createDynamicComponent(
    () => import('@/components/admin_forms/order_approval_limits/OrderApprovalLimits'),
  ),
  regionCodes: createDynamicComponent(() => import('@/components/admin_forms/region_codes/RegionCodes')),
  equipmentCodes: createDynamicComponent(() => import('@/components/admin_forms/equipment_codes/EquipmentCodes')),
  commentCaptions: createDynamicComponent(() => import('@/components/admin_forms/comment_captions/CommentCaptions')),
  customsBroker: createDynamicComponent(() => import('@/components/admin_forms/customs_broker/CustomsBroker')),
  mpsPriceFloor: createDynamicComponent(() => import('@/components/admin_forms/mps_price_floor/MpsPriceFloor')),
  mpsBaseRates: createDynamicComponent(() => import('@/components/admin_forms/mps_base_rates/MpsBaseRates')),
  ReceivePrint: createDynamicComponent(() => import('@/components/module/receive_print/receivePrint')),
  changeAssetStatus: createDynamicComponent(() => import('@/components/module/change_asset_status/ChangeAssetStatus')),
  requestTracking: createDynamicComponent(() => import('@/components/module/requestTracking/RequestTracking')),
  selectPrinter: createDynamicComponent(() => import('@/components/module/select_printer/SelectPrinter')),
  users: createDynamicComponent(() => import('@/components/module/users/UserList')),
  userUpdate: createDynamicComponent(() => import('@/components/module/users/UserUpdate')),
  creditCardPrefill: createDynamicComponent(() => import('@/components/module/credit_card_prefill/creditCardPrefill')),
  Frmglar: createDynamicComponent(() => import('@/components/admin_forms/frmglar/frmglar')),
  lostReasonsCodes: createDynamicComponent(
    () => import('@/components/admin_forms/lost_reasons_codes/LostReasonsCodes'),
  ),
  customerHoldExclusion: createDynamicComponent(
    () => import('@/components/admin_forms/customer_hold_exclusion/CustomerHoldExclusion'),
  ),
  mpsAdvancedCodes: createDynamicComponent(
    () => import('@/components/admin_forms/mps_advanced_codes/MpsAdvancedCodes'),
  ),
  equipmentProducts: createDynamicComponent(
    () => import('@/components/admin_forms/equipment_products/EquipmentProducts'),
  ),
  freightRates: createDynamicComponent(() => import('@/components/admin_forms/freight_rates/FreightRates')),
  modelSubstitutes: createDynamicComponent(() => import('@/components/admin_forms/model_substitues/ModelSubstitues')),
  autoEmailMaintenance: createDynamicComponent(
    () => import('@/components/admin_forms/auto_email_maintenance/AutoEmailMaintenance'),
  ),
  dimWeight: createDynamicComponent(() => import('@/components/admin_forms/dim_weight/DimWeight')),
  assetGroup: createDynamicComponent(() => import('@/components/admin_forms/asset_group/assetGroup')),
  creditLineApproval: createDynamicComponent(
    () => import('@/components/admin_forms/credit_line_approval/CreditLineApproval'),
  ),
};
export default componentRegistry;
